import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { downloadBlobFromResponse, getCsrfValue } from "~/common/utils/ApiHelper";
import type { RequestAQuoteFormData } from "~/domains/configurator/components/dialogs";
import { resolveApiUrl } from "~/utils/url/urlResolver";

export interface ConfiguratorV2AddToCartRequest {
  configId: string;
  currentStepNumber: number;
  quantity: number;
  priceReference: string;
}

export interface ConfiguratorV2AddToCartResponseItem {
  pk: number;
  sapMaterialNumber: string;
  designation: string;
  productCode: string;
  packCode: string;
  packSize: number;
  quantity: number;
  priceReference?: string;
}

export interface ConfiguratorV2AddToCartResponse {
  configId: string;
  responseItems: ConfiguratorV2AddToCartResponseItem[];
}

export interface ConfiguratorV2PdfData {
  designation: string;
  description: string;
  manufacturerLogoUrl: string;
  brandLogoUrl: string;
  imageUrl: string;
  steps: Array<{
    title: string;
    substepData: Array<{
      name: string;
      headerRow: string[];
      dataRows: Array<{ items: Array<{ value: string; standard: boolean }> }>;
    }>;
  }>;
}

const postJson = async <TResponse>(path: string, body: unknown, operation: string): Promise<TResponse> => {
  const response = await fetch(resolveApiUrl(path), {
    body: JSON.stringify(body),
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    method: "POST",
  });
  if (!response.ok) {
    throw new Error(`${operation} failed with status ${response.status}.`);
  }
  return response.json() as Promise<TResponse>;
};

export const addConfiguratorV2ToCart = (request: ConfiguratorV2AddToCartRequest) =>
  postJson<ConfiguratorV2AddToCartResponse>("/cpq/v2/add-to-cart", request, "Add configured product to cart");

export const saveConfiguratorV2 = (productCode: string, configId: string, name: string, currentStepNumber: number) =>
  postJson<{ code: string }>(
    `/${productCode}/configuratorPage/LinearConfigurator/save`,
    { id: configId, name, currentStep: currentStepNumber },
    "Save configuration",
  );

export const requestConfiguratorV2Quote = async (
  productCode: string,
  configId: string,
  recipientNote: string,
  currentStepNumber: number,
  designation: string,
) => {
  const response = await occFetch(occUrl("/requestLinearConfigurationQuote"), {
    body: JSON.stringify({
      productCode,
      configurationCode: configId,
      recipientNote,
      currentStepNumber,
      designation,
    }),
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Request configured-product quote failed with status ${response.status}.`);
  }
};

const toPdfWireData = (data: ConfiguratorV2PdfData) => data;

export const requestConfiguratorV2Quotation = async (
  productCode: string,
  quantity: number,
  formData: RequestAQuoteFormData,
  pdfData: ConfiguratorV2PdfData,
) => {
  const response = await occFetch(occUrl(`/configurator/${productCode}/contact-form/submit`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ formData, pdfData: toPdfWireData(pdfData), quantity }),
  });
  if (!response.ok) {
    throw new Error(`Request quotation failed with status ${response.status}.`);
  }
};

export const downloadConfiguratorV2Datasheet = async (productCode: string, pdfData: ConfiguratorV2PdfData) => {
  const response = await occFetch(occUrl(`/configurator/${productCode}/download/datasheet`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toPdfWireData(pdfData)),
  });
  if (!response.ok) {
    throw new Error(`Generate datasheet failed with status ${response.status}.`);
  }
  downloadBlobFromResponse(response);
};
